import React from 'react';

export type ScopeStakeholderType = 'portal_contact' | 'entra_group' | 'sharepoint_group' | 'email';
export type ScopeStakeholderBindingMode = 'audit_stakeholder_suggestion' | 'notification_default' | 'assignment_default';
export type ScopeStakeholderDirtyState = 'new' | 'edited' | 'removed';

export interface EditableStakeholder {
  __localId: string;
  __dirty?: ScopeStakeholderDirtyState;
  scopeEntryKey: string | null;
  stakeholderType: ScopeStakeholderType;
  stakeholderRef: string;
  displayName: string | null;
  bindingMode: ScopeStakeholderBindingMode;
  inheritsToChildren: boolean;
}

interface ScopeStakeholderListProps {
  selectedKey: string | null;
  stakeholders: EditableStakeholder[];
  entryTitleByKey: Map<string, string>;
  entryAncestors: string[];
  readOnly: boolean;
  onAdd: (binding: Omit<EditableStakeholder, '__localId' | '__dirty'>) => void;
  onUpdate: (localId: string, patch: Partial<EditableStakeholder>) => void;
  onRemove: (localId: string) => void;
}

const STAKEHOLDER_TYPE_LABELS: Record<ScopeStakeholderType, string> = {
  email: 'Email',
  portal_contact: 'Portal contact',
  entra_group: 'Entra group',
  sharepoint_group: 'SharePoint group'
};

const STAKEHOLDER_REF_HELP: Record<ScopeStakeholderType, string> = {
  email: 'jane@example.com',
  portal_contact: 'jane@example.com',
  entra_group: 'Entra object ID (GUID)',
  sharepoint_group: 'id:42, title:Auditors, or site:/sites/audit|group:Auditors'
};

const BINDING_MODE_LABELS: Record<ScopeStakeholderBindingMode, string> = {
  audit_stakeholder_suggestion: 'Suggest as Audit stakeholders',
  notification_default: 'Use for notifications',
  assignment_default: 'Use for assignments'
};

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SP_GROUP_RE = /^(id:\d+|title:.+|site:[^|]+\|group:.+)$/;

const validateRef = (type: ScopeStakeholderType, ref: string): string | null => {
  const trimmed = ref.trim();
  if (!trimmed) return 'Reference is required.';
  if ((type === 'email' || type === 'portal_contact') && !EMAIL_RE.test(trimmed)) {
    return 'Looks wrong — expected an email address.';
  }
  if (type === 'entra_group' && !GUID_RE.test(trimmed)) {
    return 'Looks wrong — expected an Entra object GUID.';
  }
  if (type === 'sharepoint_group' && !SP_GROUP_RE.test(trimmed)) {
    return 'Use id:42, title:Group name, or site:/path|group:Group name.';
  }
  return null;
};

const newEmptyBinding = (selectedKey: string | null): Omit<EditableStakeholder, '__localId' | '__dirty'> => ({
  scopeEntryKey: selectedKey,
  stakeholderType: 'email',
  stakeholderRef: '',
  displayName: null,
  bindingMode: 'audit_stakeholder_suggestion',
  inheritsToChildren: false
});

/**
 * Stakeholder bindings for the selected entry. Renders:
 *
 * - Direct bindings (scopeEntryKey === selectedKey): editable.
 * - Inherited bindings from ancestors (inheritsToChildren === true):
 *   read-only at this level, shown with the ancestor's title so the
 *   admin sees where they originate.
 *
 * Bindings carry a __localId for React keys and __dirty markers
 * ('new' | 'edited' | 'removed'). The save flow elsewhere strips
 * __localId and __dirty and filters out removed bindings before
 * sending the revision-publish payload.
 *
 * Scope-wide bindings (scopeEntryKey === null) are intentionally not
 * edited through a separate selection. When selectedKey is null, this
 * component edits bindings that apply to the whole Scope.
 */
export const ScopeStakeholderList: React.FC<ScopeStakeholderListProps> = ({
  selectedKey,
  stakeholders,
  entryTitleByKey,
  entryAncestors,
  readOnly,
  onAdd,
  onUpdate,
  onRemove
}) => {
  const [addingDraft, setAddingDraft] = React.useState<Omit<EditableStakeholder, '__localId' | '__dirty'> | null>(null);
  const [addError, setAddError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setAddingDraft(null);
    setAddError(null);
  }, [selectedKey]);

  const targetLabel = selectedKey
    ? entryTitleByKey.get(selectedKey) ?? selectedKey
    : 'Whole Scope';
  const isScopeWideTarget = selectedKey === null;

  const direct = stakeholders.filter((binding) => (
    binding.__dirty !== 'removed'
    && (binding.scopeEntryKey ?? null) === selectedKey
  ));
  const ancestorSet = new Set(entryAncestors);
  const inherited = stakeholders.filter((binding) => (
    binding.__dirty !== 'removed'
    && selectedKey !== null
    && (
      binding.scopeEntryKey === null
      || (
        binding.scopeEntryKey !== selectedKey
        && ancestorSet.has(binding.scopeEntryKey)
        && binding.inheritsToChildren
      )
    )
  ));
  const directIdentity = new Set(direct.map((binding) => (
    `${binding.stakeholderType}|${binding.stakeholderRef.trim().toLowerCase()}|${binding.bindingMode}`
  )));

  const copyInheritedToEntry = (binding: EditableStakeholder): void => {
    if (!selectedKey) return;
    onAdd({
      scopeEntryKey: selectedKey,
      stakeholderType: binding.stakeholderType,
      stakeholderRef: binding.stakeholderRef,
      displayName: binding.displayName,
      bindingMode: binding.bindingMode,
      inheritsToChildren: false
    });
  };

  const submitAddDraft = (): void => {
    if (!addingDraft) return;
    const refError = validateRef(addingDraft.stakeholderType, addingDraft.stakeholderRef);
    if (refError) {
      setAddError(refError);
      return;
    }
    setAddError(null);
    onAdd({
      ...addingDraft,
      stakeholderRef: addingDraft.stakeholderRef.trim(),
      displayName: addingDraft.displayName?.trim() || null
    });
    setAddingDraft(null);
  };

  return (
    <div className="scope-stakeholders">
      <div className="scope-stakeholders-header">
        <div>
          <h4>Stakeholders</h4>
          <div className="scope-stakeholders-target">
            {isScopeWideTarget ? 'Scope-wide defaults' : `For "${targetLabel}"`}
          </div>
        </div>
        {!readOnly && !addingDraft && (
          <button
            className="btn secondary btn-small"
            type="button"
            onClick={() => setAddingDraft(newEmptyBinding(selectedKey))}
          >
            Add stakeholder
          </button>
        )}
      </div>

      {direct.length === 0 && inherited.length === 0 && !addingDraft && (
        <div className="scope-stakeholders-empty">
          {isScopeWideTarget
            ? 'No stakeholders bound to the whole Scope.'
            : 'No stakeholders bound to this entry.'}
        </div>
      )}

      {inherited.length > 0 && (
        <div className="scope-stakeholders-section">
          <div className="scope-stakeholders-section-label">Inherited</div>
          <ul>
            {inherited.map((binding) => {
              const sourceTitle = binding.scopeEntryKey
                ? entryTitleByKey.get(binding.scopeEntryKey) ?? '(unknown ancestor)'
                : 'Whole Scope';
              const bindingIdentity = `${binding.stakeholderType}|${binding.stakeholderRef.trim().toLowerCase()}|${binding.bindingMode}`;
              const alreadyLocal = directIdentity.has(bindingIdentity);
              return (
                <li key={`inherited-${binding.__localId}`} className="scope-stakeholder-row is-inherited">
                  <div className="row-main">
                    <span className="display">{binding.displayName || binding.stakeholderRef}</span>
                    <span className="muted">
                      {STAKEHOLDER_TYPE_LABELS[binding.stakeholderType]} · {BINDING_MODE_LABELS[binding.bindingMode]}
                    </span>
                  </div>
                  <div className="row-controls">
                    <span className="badge-subtle">From "{sourceTitle}"</span>
                    {!readOnly && selectedKey && (
                      <button
                        className="btn secondary btn-small"
                        type="button"
                        onClick={() => copyInheritedToEntry(binding)}
                        disabled={alreadyLocal}
                        title={alreadyLocal
                          ? 'This entry already has the same stakeholder binding.'
                          : 'Copy this inherited binding to this entry so it can be edited independently.'}
                        aria-label={alreadyLocal
                          ? 'This entry already has the same stakeholder binding.'
                          : `Break inheritance for ${binding.displayName || binding.stakeholderRef}`}
                      >
                        Break inheritance
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {direct.length > 0 && (
        <ul>
          {direct.map((binding) => (
            <li key={binding.__localId} className={`scope-stakeholder-row${binding.__dirty ? ` is-${binding.__dirty}` : ''}`}>
              <div className="row-main">
                <span className="display">{binding.displayName || binding.stakeholderRef}</span>
                <span className="muted">
                  {STAKEHOLDER_TYPE_LABELS[binding.stakeholderType]} · {binding.stakeholderRef}
                </span>
                {binding.__dirty === 'new' && <span className="dirty-flag flag-new">new</span>}
                {binding.__dirty === 'edited' && <span className="dirty-flag flag-renamed">edited</span>}
              </div>
              <div className="row-controls">
                <label>
                  <span className="muted">Use as</span>
                  <select
                    className="input input-small"
                    value={binding.bindingMode}
                    disabled={readOnly}
                    onChange={(event) => onUpdate(binding.__localId, {
                      bindingMode: event.target.value as ScopeStakeholderBindingMode
                    })}
                  >
                    {Object.entries(BINDING_MODE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>
                <label className="inherits-toggle">
                  <input
                    type="checkbox"
                    checked={binding.inheritsToChildren}
                    disabled={readOnly}
                    onChange={(event) => onUpdate(binding.__localId, {
                      inheritsToChildren: event.target.checked
                    })}
                  />
                  <span>Inherits to children</span>
                </label>
                {!readOnly && (
                  <button
                    className="btn secondary btn-small"
                    type="button"
                    onClick={() => onRemove(binding.__localId)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {addingDraft && (
        <div className="scope-stakeholder-add">
          <div className="row">
            <label>
              <span className="muted">Type</span>
              <select
                className="input"
                value={addingDraft.stakeholderType}
                onChange={(event) => setAddingDraft((previous) => previous ? {
                  ...previous,
                  stakeholderType: event.target.value as ScopeStakeholderType
                } : previous)}
              >
                {Object.entries(STAKEHOLDER_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="grow">
              <span className="muted">Reference</span>
              <input
                className="input"
                type="text"
                value={addingDraft.stakeholderRef}
                placeholder={STAKEHOLDER_REF_HELP[addingDraft.stakeholderType]}
                onChange={(event) => setAddingDraft((previous) => previous ? {
                  ...previous,
                  stakeholderRef: event.target.value
                } : previous)}
              />
            </label>
          </div>
          <div className="row">
            <label className="grow">
              <span className="muted">Display name (optional)</span>
              <input
                className="input"
                type="text"
                value={addingDraft.displayName ?? ''}
                onChange={(event) => setAddingDraft((previous) => previous ? {
                  ...previous,
                  displayName: event.target.value
                } : previous)}
              />
            </label>
            <label>
              <span className="muted">Use as</span>
              <select
                className="input"
                value={addingDraft.bindingMode}
                onChange={(event) => setAddingDraft((previous) => previous ? {
                  ...previous,
                  bindingMode: event.target.value as ScopeStakeholderBindingMode
                } : previous)}
              >
                {Object.entries(BINDING_MODE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="inherits-toggle">
            <input
              type="checkbox"
              checked={addingDraft.inheritsToChildren}
              onChange={(event) => setAddingDraft((previous) => previous ? {
                ...previous,
                inheritsToChildren: event.target.checked
              } : previous)}
            />
            <span>Inherits to descendant entries</span>
          </label>
          {addError && <div className="muted" style={{ color: 'var(--danger)' }}>{addError}</div>}
          <div className="add-actions">
            <button
              className="btn secondary btn-small"
              type="button"
              onClick={() => { setAddingDraft(null); setAddError(null); }}
            >
              Cancel
            </button>
            <button
              className="btn btn-small"
              type="button"
              onClick={submitAddDraft}
            >
              Add stakeholder
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScopeStakeholderList;
